import React, { useEffect, useState } from 'react';
import onboardingTourService, { type MetadataStep } from '../../services/onboardingTourService';
import { useOnboardingTour } from '../../hooks/useOnboardingTour';

interface TooltipPosition {
  top: number;
  left: number;
}

const TOOLTIP_WIDTH = 252;
const TOOLTIP_HEIGHT_ESTIMATE = 180;
const GAP = 12;
const NEW_RECENT_TABS_SELECTOR = '[data-tour-id="new-recent-tabs"]';
const WORKSPACE_TOOLS_SELECTOR = '[data-tour-id="workspace-tools-panels"]';
const CONTEXT_UTILITY_SELECTOR = '.nav-header__right [data-tour-id="workspace-tools-panels"][data-utility="Context"]';
const ACTIVITY_NEW_AGENT_SELECTOR = '[data-tour-id="activity-new-agent-btn"]';
const ACTIVITY_CREATE_TASK_SELECTOR = '[data-tour-id="activity-create-task-btn"]';
// The welcome-tutorial button lives inside the settings fly-in panel which is
// conditionally rendered — it must be found via this exact selector after the
// panel is opened.
const WELCOME_TUTORIAL_SELECTOR = '[data-tour-id="welcome-tutorial-btn"]';

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

function isVisibleElement(element: Element): element is HTMLElement {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);
  return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none';
}

function isElementInViewport(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  return rect.bottom > 0
    && rect.right > 0
    && rect.top < window.innerHeight
    && rect.left < window.innerWidth;
}

function resolveStepTarget(selector: string): HTMLElement | null {
  if (!selector.trim()) {
    return null;
  }

  try {
    const candidates = Array.from(document.querySelectorAll(selector));
   const visible = candidates.find((candidate): candidate is HTMLElement => isVisibleElement(candidate) && isElementInViewport(candidate));
    if (visible) {
      return visible;
    }

    return null;
  } catch {
    return null;
  }
}

function resolveAllVisibleTargets(selector: string): HTMLElement[] {
  if (!selector.trim()) {
    return [];
  }

  try {
    return Array.from(document.querySelectorAll(selector)).filter(
      (el): el is HTMLElement => el instanceof HTMLElement && isVisibleElement(el) && isElementInViewport(el)
    );
  } catch {
    return [];
  }
}

function normalizeSelector(selector: string): string {
  const token = selector.trim();
  if (!token) {
    return '';
  }

  if (
    token.startsWith('[')
    || token.startsWith('.')
    || token.startsWith('#')
    || token.includes(' ')
    || token.includes('>')
    || token.includes(':')
  ) {
    return token;
  }

  if (/^[a-zA-Z0-9_-]+$/.test(token)) {
    return `[data-tour-id="${token}"]`;
  }

  return token;
}

function resolveSelectorForStep(step: MetadataStep): string {
  const tokens = [step.id, step.stepId, step.element]
    .map((value) => String(value ?? '').trim().toLowerCase())
    .filter((value) => !!value);

  if (tokens.some((value) =>
    value.includes('activity-new-agent-btn')
    || value.includes('new-agent')
    || value === 'newagent'
  )) {
    return ACTIVITY_NEW_AGENT_SELECTOR;
  }

  if (tokens.some((value) =>
    value.includes('activity-create-task-btn')
    || value.includes('activity-create-task')
  )) {
    return ACTIVITY_CREATE_TASK_SELECTOR;
  }

  // Welcome-tutorial button is inside the settings fly-in — always use the
  // known constant so normalization is applied regardless of how the API
  // encodes the step id / element.
  if (tokens.some((value) =>
    value.includes('welcome-tutorial')
    || value.includes('profile-header')
  )) {
    return WELCOME_TUTORIAL_SELECTOR;
  }

  const explicit = String(step.element ?? '').trim();
  if (explicit) {
    return normalizeSelector(explicit);
  }

  const fallback = String(step.stepId ?? step.id ?? '').trim();
  return normalizeSelector(fallback);
}

function isWorkspaceToolsStep(step: MetadataStep): boolean {
  const id = (step.id ?? '').toLowerCase();
  const stepId = (step.stepId ?? '').toLowerCase();
  const title = (step.title ?? '').toLowerCase();
  const element = (step.element ?? '').toLowerCase();

  return (
    id === 'workspace-tools-panels' ||
    stepId === 'workspace-tools-panels' ||
    title === 'all tools at your fingertips' ||
    element.includes('workspace-tools-panels')
  );
}

function isWelcomeTutorialButtonStep(step: MetadataStep): boolean {
  const id = (step.id ?? '').toLowerCase();
  const stepId = (step.stepId ?? '').toLowerCase();
  const element = (step.element ?? '').toLowerCase();

  return (
    id.includes('profile-header') ||
    stepId.includes('profile-header') ||
    id.includes('welcome-tutorial') ||
    stepId.includes('welcome-tutorial') ||
    element.includes('welcome-tutorial-btn')
  );
}

function resolveStepTargetForStep(step: MetadataStep): HTMLElement | null {
  if (isWelcomeTutorialButtonStep(step)) {
    // The welcome-tutorial button lives inside the settings fly-in panel which
    // is conditionally rendered (only exists in the DOM when the panel is open).
    // Always use the normalized constant selector — step.element may be an
    // unnormalized plain ID like 'welcome-tutorial-btn' which would fail as a
    // CSS selector.
    const existingTarget = resolveStepTarget(WELCOME_TUTORIAL_SELECTOR);
    if (existingTarget) {
      return existingTarget;
    }

    // Panel is closed — programmatically open the settings fly-in so the
    // button is rendered in the DOM.  The target will be found on the next
    // animation-frame retry once React has re-rendered.
    //
    // Dispatch a direct custom event that MainHeader listens to and calls
    // setActiveFlyinMode('settings') directly.  This avoids the race between
    // a programmatic .click() and MainHeader's handleDocumentClick listener
    // (which can close the panel in the same synchronous tick if headerRef is
    // not resolved at click time).
    const alreadyOpen = !!document.querySelector(WELCOME_TUTORIAL_SELECTOR);
    if (!alreadyOpen) {
      window.dispatchEvent(new CustomEvent('kci-tour-open-settings-panel'));
    }

    return null;
  }

  if (isWorkspaceToolsStep(step)) {
    const strictContextCandidates = resolveAllVisibleTargets(CONTEXT_UTILITY_SELECTOR);
    if (strictContextCandidates.length > 0) {
      return strictContextCandidates[0];
    }

    const workspaceCandidates = resolveAllVisibleTargets(WORKSPACE_TOOLS_SELECTOR);
    if (workspaceCandidates.length > 0) {
      return workspaceCandidates.sort((a, b) => {
        const ar = a.getBoundingClientRect();
        const br = b.getBoundingClientRect();
        if (ar.top !== br.top) {
          return ar.top - br.top;
        }
        return br.right - ar.right;
      })[0];
    }
  }

  const selector = resolveSelectorForStep(step);
  if (selector) {
    return resolveStepTarget(selector);
  }

  return null;
}

function computePosition(target: DOMRect, position: MetadataStep['position']): TooltipPosition {
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  let left = target.left;
  let top = target.top;

  switch (position) {
    case 'top':
      top = target.top - TOOLTIP_HEIGHT_ESTIMATE - GAP;
      left = target.left + (target.width / 2) - (TOOLTIP_WIDTH / 2);
      break;
    case 'bottom':
    case 'bottom-center':
      top = target.bottom + GAP;
      left = target.left + (target.width / 2) - (TOOLTIP_WIDTH / 2);
      break;
    case 'left':
      top = target.top + (target.height / 2) - (TOOLTIP_HEIGHT_ESTIMATE / 2);
      left = target.left - TOOLTIP_WIDTH - GAP;
      break;
    case 'right':
      top = target.top + (target.height / 2) - (TOOLTIP_HEIGHT_ESTIMATE / 2);
      left = target.right + GAP;
      break;
    case 'center':
    default:
      top = target.top + (target.height / 2) - (TOOLTIP_HEIGHT_ESTIMATE / 2);
      left = target.left + (target.width / 2) - (TOOLTIP_WIDTH / 2);
      break;
  }

  return {
    top: clamp(top, 12, viewportH - TOOLTIP_HEIGHT_ESTIMATE - 12),
    left: clamp(left, 12, viewportW - TOOLTIP_WIDTH - 12),
  };
}

export const TourOverlay: React.FC = () => {
  const { currentStep, currentStepIndex, totalSteps, isActive, isRestart, currentFeature } = useOnboardingTour();
  const POPUP_TRANSITION_MS = 400;

  const [popupPosition, setPopupPosition] = useState<TooltipPosition | null>(null);
  const [displayStep, setDisplayStep] = useState<MetadataStep | null>(null);
  const [displayStepIndex, setDisplayStepIndex] = useState(0);
  const [isWaitingForTarget, setIsWaitingForTarget] = useState(false);

  const pendingStepRef = React.useRef<{ step: MetadataStep; stepIndex: number } | null>(null);
  const navigationIdRef = React.useRef(0);
  const contentTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstShowRef = React.useRef(true);

  const clearContentTimer = () => {
    if (contentTimerRef.current !== null) {
      clearTimeout(contentTimerRef.current);
      contentTimerRef.current = null;
    }
  };

  useEffect(() => {
    const navigationId = ++navigationIdRef.current;
    let cancelled = false;

    clearContentTimer();
    pendingStepRef.current = null;

    if (!isActive || !currentStep) {
      isFirstShowRef.current = true;
      queueMicrotask(() => {
        if (!cancelled) setIsWaitingForTarget(false);
      });
      return () => {
        cancelled = true;
      };
    }

    let rafId: number | null = null;

    const movePopup = (position: TooltipPosition) => {
      if (cancelled || navigationIdRef.current !== navigationId) {
        return;
      }

      setIsWaitingForTarget(false);

      if (isFirstShowRef.current) {
        isFirstShowRef.current = false;
        setPopupPosition(position);
        setDisplayStep(currentStep);
        setDisplayStepIndex(currentStepIndex);
        return;
      }

      // On navigation, move first and delay content swap until movement finishes.
      pendingStepRef.current = { step: currentStep, stepIndex: currentStepIndex };
      setPopupPosition(position);

      contentTimerRef.current = setTimeout(() => {
        if (cancelled || navigationIdRef.current !== navigationId || !pendingStepRef.current) {
          return;
        }

        setDisplayStep(pendingStepRef.current.step);
        setDisplayStepIndex(pendingStepRef.current.stepIndex);
        pendingStepRef.current = null;
        contentTimerRef.current = null;
      }, POPUP_TRANSITION_MS);
    };

    const stepSelector = resolveSelectorForStep(currentStep);

    if (currentStep.modal === 'popover' || !stepSelector) {
      movePopup({
        top: Math.max(24, (window.innerHeight - TOOLTIP_HEIGHT_ESTIMATE) / 2),
        left: Math.max(12, (window.innerWidth - TOOLTIP_WIDTH) / 2),
      });

      return () => {
        cancelled = true;
        if (rafId !== null) {
          window.cancelAnimationFrame(rafId);
        }
        clearContentTimer();
      };
    }

    let attempts = 0;
    const maxAttempts = 180;

    const resolveWithRetry = () => {
      if (cancelled || navigationIdRef.current !== navigationId || !stepSelector) {
        return;
      }

      const target = resolveStepTargetForStep(currentStep);
      if (target) {
        movePopup(computePosition(target.getBoundingClientRect(), currentStep.position || 'center'));
        return;
      }

      setIsWaitingForTarget(true);

      if (attempts < maxAttempts) {
        attempts += 1;
        rafId = window.requestAnimationFrame(resolveWithRetry);
      }
    };

    rafId = window.requestAnimationFrame(resolveWithRetry);

    return () => {
      cancelled = true;
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      clearContentTimer();
    };
  }, [currentStep, currentStepIndex, isActive]);

  useEffect(() => {
    const step = displayStep;
    const stepSelector = step ? resolveSelectorForStep(step) : '';
    if (!step || !stepSelector || step.modal === 'popover') {
      return;
    }

    const updatePosition = () => {
      if (!stepSelector) {
        return;
      }
      const target = resolveStepTargetForStep(step);
      if (target) {
        setPopupPosition(computePosition(target.getBoundingClientRect(), step.position || 'center'));
      }
    };

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [displayStep]);

  useEffect(() => {
    return () => {
      clearContentTimer();
    };
  }, []);

  if (!isActive || !displayStep || !popupPosition) {
    return null;
  }

  const isNewRecentTabsStep =
    (displayStep.element ?? '').toLowerCase().includes(NEW_RECENT_TABS_SELECTOR.slice(1, -1)) ||
    (displayStep.stepId ?? '').toLowerCase().includes('newrecenttabs') ||
    (displayStep.stepId ?? '').toLowerCase() === 'new-recent-tabs' ||
    (displayStep.id ?? '').toLowerCase().includes('profile-header') ||
    (displayStep.element ?? '').toLowerCase().includes('welcome-tutorial-btn');

  const isLastStep = totalSteps > 0 && displayStepIndex === totalSteps - 1;

  const handleNextStep = () => {
    if (isLastStep && displayStep.element && !isRestart) {
      const featureKey = currentFeature;
      if (featureKey) {
        void onboardingTourService.updateFeatureCompletion(featureKey);
      }
    }
    onboardingTourService.nextStep();
  };
  const arrowBaseStyle: React.CSSProperties = {
    position: 'absolute',
    width: 0,
    height: 0,
  };

  const arrowStyle: React.CSSProperties = (() => {
    const pos = displayStep.position ?? 'left';
    const arrowColor = isNewRecentTabsStep ? '#ffffff' : '#0b4fd9';
    if (pos === 'right') {
      return {
        ...arrowBaseStyle,
        left: '-8px',
        top: '42px',
        borderTop: '8px solid transparent',
        borderBottom: '8px solid transparent',
        borderRight: `8px solid ${arrowColor}`,
      };
    }
    if (pos === 'top') {
      return {
        ...arrowBaseStyle,
        left: '32px',
        bottom: '-8px',
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderTop: `8px solid ${arrowColor}`,
      };
    }
    if (pos === 'bottom' || pos === 'bottom-center') {
      return {
        ...arrowBaseStyle,
        left: '32px',
        top: '-8px',
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderBottom: `8px solid ${arrowColor}`,
      };
    }

    return {
      ...arrowBaseStyle,
      right: '-8px',
      top: '42px',
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
      borderLeft: `8px solid ${arrowColor}`,
    };
  })();

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 18, 44, 0.04)',
          zIndex: 9998,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Onboarding tour"
        data-tour-overlay="true"
        data-tour-loading={isWaitingForTarget ? 'true' : 'false'}
        style={{
          position: 'fixed',
          zIndex: 9999,
          top: `${popupPosition.top}px`,
          left: `${popupPosition.left}px`,
          transition: `top ${POPUP_TRANSITION_MS}ms ease, left ${POPUP_TRANSITION_MS}ms ease`,
          width: `${TOOLTIP_WIDTH}px`,
          borderRadius: '8px',
          background: isNewRecentTabsStep ? '#ffffff' : '#0b4fd9',
          boxShadow: 'rgb(8 35 107 / 9%) 0px 14px 28px',
          border: isNewRecentTabsStep ? '1px solid #d7e3ff' : '1px solid rgba(255, 255, 255, 0.15)',
          padding: '14px 14px 12px',
          color: isNewRecentTabsStep ? '#0a2558' : '#ffffff',
          opacity: isWaitingForTarget ? 0.92 : 1,
        }}
      >
        <div style={arrowStyle} aria-hidden="true" />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
          <div style={{ fontSize: isNewRecentTabsStep ? '16px' : '26px', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.2px' }}>{displayStep.title}</div>
          <button
            type="button"
            onClick={() => onboardingTourService.skipFeatureTour()}
            aria-label="Close tour"
            style={{
              border: 0,
              background: 'transparent',
              cursor: 'pointer',
              fontSize: '18px',
              lineHeight: 1,
              color: isNewRecentTabsStep ? '#0b4fd9' : '#ffffff',
              opacity: 0.92,
              padding: 0,
            }}
          >
            ×
          </button>
        </div>

        <p style={{ margin: '10px 0px 12px', fontSize: '12px', lineHeight: 1.45, color: isNewRecentTabsStep ? '#0c233ce0' : 'rgba(255,255,255,0.95)' }}>{displayStep.description}</p>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: isNewRecentTabsStep ? '#1b3f84' : 'rgba(255,255,255,0.95)' }}>
            {totalSteps > 0 ? `${displayStepIndex + 1} of ${totalSteps}` : ''}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => onboardingTourService.previousStep()}
              disabled={displayStepIndex === 0}
              style={{
                borderRadius: '4px',
                border: isNewRecentTabsStep ? '1px solid #b8cdfa' : '1px solid rgba(255,255,255,0.34)',
                background: isNewRecentTabsStep ? '#ffffff' : 'rgba(255,255,255,0.12)',
                color: isNewRecentTabsStep ? '#1b3f84' : '#ffffff',
                fontSize: '13px',
                fontWeight: 600,
                minWidth: '54px',
                padding: '5px 10px',
                cursor: displayStepIndex === 0 ? 'not-allowed' : 'pointer',
                opacity: displayStepIndex === 0 ? 0.45 : 0.95,
              }}
            >
              {displayStep.backButtonText ?? 'Back'}
            </button>
            <button
              type="button"
              onClick={handleNextStep}
              style={{
                borderRadius: '4px',
                border: isNewRecentTabsStep ? '1px solid #0b4fd9' : '1px solid #ffffff',
                background: isNewRecentTabsStep ? '#0b4fd9' : '#ffffff',
                color: isNewRecentTabsStep ? '#ffffff' : '#0b4fd9',
                fontSize: '13px',
                fontWeight: 700,
                minWidth: '54px',
                padding: '5px 10px',
                cursor: 'pointer',
              }}
            >
              {isLastStep ? 'Close' : (displayStep.nextButtonText ?? 'Next')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TourOverlay;
